import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../actions/userActions";
import SiteImage from "./SiteImage";

const Header = () => {
  const dispatch = useDispatch();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const windowWidth = useRef(window.innerWidth);
  const [heroLayout, setHeroLayout] = useState("side"); // "side" | "stack"

  const logoutHandler = () => {
    dispatch(logout());
  };

  const handleHeroLoad = ({ ratio }) => {
    // if the image is wider than tall, stack it above the text on desktop
    setHeroLayout(ratio >= 1.3 ? "stack" : "side");
  };

  /* Smooth-scroll to the category-select + products section on the same page.
     Falls through to the Link's default navigation if the target isn't on screen. */
  const handleExploreClick = (e) => {
    const target = document.getElementById("hadacy-explore");
    if (target) {
      e.preventDefault();
      const y = target.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const isDesktop = windowWidth.current > 600;
  const useStack = isDesktop && heroLayout === "stack";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@200;300;400;500&display=swap');

        .hadacy-hero {
          --cream: #f5f0e8;
          --dark: #1a1410;
          --gold: #b8933a;
          --gold-light: #d4af6a;
          --muted: #7a6e62;
          --card-bg: #faf7f2;

          background: var(--cream);
          color: var(--dark);
          font-family: "Jost", sans-serif;
          font-weight: 300;
          padding: 3rem 0 2.5rem;
          position: relative;
          overflow: hidden;

          /* Full-bleed: extend cream background to viewport edges,
             escaping any constrained parent (Bootstrap Container, etc.) */
          width: 100vw;
          left: 50%;
          right: 50%;
          margin-left: -50vw;
          margin-right: -50vw;

          /* Pull the hero up flush against the navbar (negate <main className="py-3"> padding-top) */
          margin-top: calc(-1rem - 1px);
        }
        .hadacy-hero::before {
          content: "";
          position: absolute;
          top: -120px; right: -120px;
          width: 360px; height: 360px;
          border-radius: 50%;
          background: radial-gradient(circle at center, rgba(184,147,58,0.18), transparent 70%);
          pointer-events: none;
        }
        .hadacy-hero::after {
          content: "";
          position: absolute;
          bottom: -180px; left: -160px;
          width: 420px; height: 420px;
          border-radius: 50%;
          background: radial-gradient(circle at center, rgba(232,213,196,0.35), transparent 70%);
          pointer-events: none;
        }

        .hadacy-hero * { box-sizing: border-box; }

        .hadacy-hero .hh-container {
          position: relative;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        .hadacy-hero .hh-grid {
          display: grid;
          grid-template-columns: 1.05fr 1fr;
          gap: 3rem;
          align-items: center;
        }
        .hadacy-hero.hh-stack .hh-grid { grid-template-columns: 1fr; gap: 2rem; }
        @media (max-width: 768px) {
          .hadacy-hero .hh-grid { grid-template-columns: 1fr; gap: 1.6rem; }
          .hadacy-hero { padding: 2rem 0 1.5rem; }
        }

        .hadacy-hero .hh-copy { display: flex; flex-direction: column; }
        .hadacy-hero .hh-kicker {
          font-size: 0.72rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.9rem;
        }
        .hadacy-hero .hh-headline {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400;
          color: var(--dark);
          font-size: clamp(2rem, 4.6vw, 3.4rem);
          line-height: 1.02;
          letter-spacing: 0.005em;
          margin: 0 0 1rem;
          text-transform: none;
        }
        .hadacy-hero .hh-headline em {
          font-style: italic;
          color: var(--gold);
          font-weight: 300;
        }
        .hadacy-hero .hh-divider {
          width: 64px;
          height: 1px;
          background: linear-gradient(90deg, var(--gold), transparent);
          margin: 0.4rem 0 1.2rem;
        }
        .hadacy-hero .hh-lead {
          font-size: 1rem;
          line-height: 1.75;
          color: var(--muted);
          max-width: 540px;
          margin: 0 0 1.4rem;
        }
        .hadacy-hero .hh-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1.6rem;
        }
        /* Hide the feature pills on phones (any orientation, incl. landscape) */
        @media (max-width: 768px), (max-height: 500px) {
          .hadacy-hero .hh-pills { display: none !important; }
        }
        .hadacy-hero .hh-pill {
          padding: 0.45rem 0.85rem;
          border: 1px solid rgba(184,147,58,0.28);
          background: rgba(255,255,255,0.6);
          border-radius: 999px;
          font-size: 0.66rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
        }
        .hadacy-hero .hh-actions { display: flex; flex-wrap: wrap; gap: 0.7rem; }
        .hadacy-hero .hh-cta-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem 1.4rem;
          border-radius: 14px;
          background: var(--dark);
          color: var(--gold-light);
          border: 1px solid var(--dark);
          letter-spacing: 0.16em;
          text-transform: uppercase;
          font-size: 0.74rem;
          text-decoration: none;
          transition: transform .2s ease, box-shadow .2s ease;
        }
        .hadacy-hero .hh-cta-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(26,20,16,0.22);
          color: var(--gold-light);
        }
        .hadacy-hero .hh-cta-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem 1.4rem;
          border-radius: 14px;
          background: transparent;
          color: var(--gold);
          border: 1px solid rgba(184,147,58,0.45);
          letter-spacing: 0.16em;
          text-transform: uppercase;
          font-size: 0.74rem;
          text-decoration: none;
          transition: all .2s ease;
        }
        .hadacy-hero .hh-cta-secondary:hover {
          background: rgba(184,147,58,0.08);
          color: var(--dark);
        }

        /* Image side */
        .hadacy-hero .hh-image-wrap {
          position: relative;
          padding: 1rem;
        }
        .hadacy-hero .hh-image-frame {
          position: relative;
          border-radius: 22px;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(184,147,58,0.10), rgba(232,213,196,0.30));
          box-shadow: 0 24px 60px rgba(26,20,16,0.16);
          aspect-ratio: 4 / 5;
        }
        .hadacy-hero.hh-stack .hh-image-frame { aspect-ratio: 16 / 7; }
        .hadacy-hero .hh-image-frame img,
        .hadacy-hero .hh-image-frame > div > img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          display: block;
          border-radius: 0 !important;
        }
        .hadacy-hero .hh-image-frame > div { width: 100%; height: 100%; }

        .hadacy-hero .hh-image-deco {
          position: absolute;
          width: 90px;
          height: 90px;
          border: 1px solid rgba(184,147,58,0.45);
          border-radius: 50%;
          pointer-events: none;
        }
        .hadacy-hero .hh-image-deco.deco-tl { top: -10px; left: -10px; }
        .hadacy-hero .hh-image-deco.deco-br { bottom: -10px; right: -10px; }

        .hadacy-hero .hh-stat-strip {
          margin-top: 2rem;
          padding-top: 1.4rem;
          border-top: 1px solid rgba(184,147,58,0.22);
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }
        @media (max-width: 600px) {
          .hadacy-hero .hh-stat-strip { grid-template-columns: 1fr 1fr; }
        }
        .hadacy-hero .hh-stat-num {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.7rem;
          color: var(--dark);
          line-height: 1;
        }
        .hadacy-hero .hh-stat-label {
          font-size: 0.65rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
          margin-top: 0.35rem;
        }
      `}</style>

      <div className={`hadacy-hero ${useStack ? "hh-stack" : ""}`}>
        <div className="hh-container">
          {isDesktop ? (
            useStack ? (
              <>
                <div className="hh-image-wrap">
                  <span className="hh-image-deco deco-tl" />
                  <div className="hh-image-frame">
                    <SiteImage
                      keyName="principal"
                      fallback={require("../img/hadacy.png")}
                      alt="Hadacy hero"
                      onImgLoad={handleHeroLoad}
                      priority
                      width={1600}
                      loaderHeight={520}
                    />
                  </div>
                  <span className="hh-image-deco deco-br" />
                </div>

                <div className="hh-copy">
                  <span className="hh-kicker">HADACY · Skin Care &amp; Parfum</span>
                  <h1 className="hh-headline">
                    Perfumes y cuidado para la piel: <em>descubre tu resplandor natural</em>
                  </h1>
                  <div className="hh-divider" />
                  <p className="hh-lead">
                    Fragancias que enamoran y cosméticos que transforman.
                    Pensados para realzar tu esencia y nutrir tu piel.
                  </p>
                  <div className="hh-pills">
                    <span className="hh-pill">Fórmulas avanzadas</span>
                    <span className="hh-pill">Ingredientes naturales</span>
                    <span className="hh-pill">Resultados visibles</span>
                  </div>
                  <div className="hh-actions">
                    <Link to="/page/1" className="hh-cta-primary" onClick={handleExploreClick}>Explorar productos</Link>
                    <Link to="/catalogo" className="hh-cta-secondary">Ver catálogo</Link>
                  </div>
                </div>
              </>
            ) : (
              <div className="hh-grid">
                <div className="hh-copy">
                  <span className="hh-kicker">HADACY · Skin Care &amp; Parfum</span>
                  <h1 className="hh-headline">
                    Perfumes y cuidado para la piel: <em>descubre tu resplandor natural</em>
                  </h1>
                  <div className="hh-divider" />
                  <p className="hh-lead">
                    Fragancias que enamoran y cosméticos que transforman.
                    Pensados para realzar tu esencia y nutrir tu piel.
                  </p>
                  <div className="hh-pills">
                    <span className="hh-pill">Fórmulas avanzadas</span>
                    <span className="hh-pill">Ingredientes naturales</span>
                    <span className="hh-pill">Resultados visibles</span>
                  </div>
                  <div className="hh-actions">
                    <Link to="/page/1" className="hh-cta-primary" onClick={handleExploreClick}>Explorar productos</Link>
                    <Link to="/catalogo" className="hh-cta-secondary">Ver catálogo</Link>
                  </div>

                  <div className="hh-stat-strip">
                    <div>
                      <div className="hh-stat-num">100+</div>
                      <div className="hh-stat-label">Aromas</div>
                    </div>
                    <div>
                      <div className="hh-stat-num">100 mL</div>
                      <div className="hh-stat-label">Por perfume</div>
                    </div>
                    <div>
                      <div className="hh-stat-num">Envío<br />Gratis</div>
                      <div className="hh-stat-label">2+ perfumes</div>
                    </div>
                  </div>
                </div>

                <div className="hh-image-wrap">
                  <span className="hh-image-deco deco-tl" />
                  <div className="hh-image-frame">
                    <SiteImage
                      keyName="principal"
                      fallback={require("../img/hadacy.png")}
                      alt="Hadacy hero"
                      onImgLoad={handleHeroLoad}
                      priority
                      width={1600}
                      loaderHeight={520}
                    />
                  </div>
                  <span className="hh-image-deco deco-br" />
                </div>
              </div>
            )
          ) : (
            // MOBILE
            <div className="hh-grid">
              <div className="hh-image-wrap">
                <span className="hh-image-deco deco-tl" />
                <div className="hh-image-frame">
                  <SiteImage
                    keyName="principal_mobile"
                    fallback={require("../img/hadacy.png")}
                    alt="Hadacy hero"
                    priority
                    width={900}
                    loaderHeight={420}
                  />
                </div>
                <span className="hh-image-deco deco-br" />
              </div>
              <div className="hh-copy">
                <span className="hh-kicker">HADACY · Skin Care &amp; Parfum</span>
                <h1 className="hh-headline">
                  Perfumes y cuidado para la piel: <em>descubre tu resplandor natural</em>
                </h1>
                <div className="hh-divider" />
                <p className="hh-lead">
                  Fragancias que enamoran y cosméticos que transforman.
                  Pensados para realzar tu esencia y nutrir tu piel.
                </p>
                <div className="hh-pills">
                  <span className="hh-pill">Fórmulas avanzadas</span>
                  <span className="hh-pill">Ingredientes naturales</span>
                  <span className="hh-pill">Resultados visibles</span>
                </div>
                <div className="hh-actions">
                  <Link to="/page/1" className="hh-cta-primary" onClick={handleExploreClick}>Explorar productos</Link>
                  <Link to="/catalogo" className="hh-cta-secondary">Ver catálogo</Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;
